import {
  assembleToggleOptions,
  WORKFLOW_TAB_INDEX,
  workflowTabPath,
} from '@/components/Modals/SetupWorkflow/SetupWorkflow.utils';
import type { SetupWorkflowFormValues } from '@/components/Modals/SetupWorkflow/SetupWorkflow';
import Switch from '@/components/UI/FormElements/Switch';
import type { ID } from '@/model/Common.model';
import { Controller, useFormContext, useWatch } from 'react-hook-form-latest';
import { SelectionReorder } from '@/components/Modals/SetupWorkflow/SelectionReorder.tsx';
import { ErrorMessage } from '@hookform/error-message';
import Loading from '@/components/Loading';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import type { StudyApiId } from '@/model/Study.model';
import type { FC } from 'react';
import { useMemo } from 'react';

interface DosingProps {
  studyApiId: StudyApiId;
}

const dosingPath = workflowTabPath(WORKFLOW_TAB_INDEX.DOSING);

interface TreatmentReorderProps {
  options: Array<{ id: ID; title: string }>;
  // The dosing tab stores treatment IDs in `workflowTabConfig[…].children`,
  // while other tabs store `WorkflowModelChild` rows. The shared form schema
  // can't express that heterogeneity, so this adapter narrows the field
  // contract once for SelectionReorder.
  fieldValue: unknown;
  onFieldChange: (next: Array<ID>) => void;
}

const TreatmentReorder: FC<TreatmentReorderProps> = ({ options, fieldValue, onFieldChange }) => (
  <SelectionReorder
    options={options}
    selectionTitle="treatment"
    selection={(fieldValue as Array<ID> | undefined) ?? []}
    selectionChange={onFieldChange}
  />
);

const Dosing: FC<DosingProps> = ({ studyApiId }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<SetupWorkflowFormValues>();

  const { response: treatmentsResponse, loading: treatmentsLoading } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/treatments',
    invokeOnInit: true,
    query: { include: ['study_groups'], perPage: -1 },
    path: { studyApiId },
  });

  const workflowTabConfig = useWatch({
    control,
    name: 'workflowSetupForm.workflowTabConfig',
  });

  const toggleOptions = useMemo(() => assembleToggleOptions(workflowTabConfig ?? [], 'dosing'), [workflowTabConfig]);

  if (treatmentsLoading) {
    return <Loading />;
  }
  const treatments: Array<{ title: string; id: ID }> =
    treatmentsResponse?.body?.data?.map((treatment) => ({
      ...treatment,
      title: treatment.treatment_label ?? '',
      id: treatment.api_id,
    })) ?? [];
  return (
    <div data-testid="workflow-dosing-setup-main">
      <div>
        <div data-test-element="treatment-selection-container">
          <Controller
            control={control}
            name={`${dosingPath}.children`}
            render={({ field: { onChange, value } }) => (
              <>
                <ErrorMessage
                  errors={errors}
                  name={`${dosingPath}.children`}
                  render={({ message }) => (
                    <p className="mt2 ml2 f6 red" data-test-element="email-error">
                      {message}
                    </p>
                  )}
                />
                <TreatmentReorder options={treatments} fieldValue={value} onFieldChange={onChange} />
              </>
            )}
          />
        </div>
      </div>
      <div className="pa3 bt b--moon-gray flex justify-between items-center">
        <label className="mb0 f6">Skip dosing for animals that do not require any treatments</label>
        <Controller
          defaultValue={false}
          name={`${dosingPath}.skipDosing`}
          control={control}
          render={({ field: { value, onChange } }) => (
            <Switch value={value ?? false} onChange={onChange} testId="skip-dosing-not-required-treatments-switch" />
          )}
        />
      </div>
      <div className="pa3 bt b--moon-gray flex justify-between items-center">
        <div>
          <label className="mb1 f6">Use new body weight</label>
          <small>For dose calculation without amending study data.</small>
        </div>
        <Controller
          defaultValue={false}
          name={`${dosingPath}.takeWeight`}
          control={control}
          render={({ field: { value, onChange, name } }) => (
            <Switch testId={name} value={value ?? false} onChange={onChange} />
          )}
        />
      </div>
      <div className="pa3 bt b--moon-gray flex justify-between items-center">
        <label className="mb0 f6">After saving</label>
        <Controller
          defaultValue="focus-search"
          name={`${dosingPath}.onSave`}
          control={control}
          render={({ field: { value, onChange } }) => (
            <select className="mw5 mb0" value={value} onChange={onChange}>
              <option value="focus-search">Focus search bar</option>
              {Object.keys(toggleOptions).map((k) => (
                <option key={k} value={toggleOptions[k].value}>
                  {toggleOptions[k].name}
                </option>
              ))}
              <option value="next-subject" data-testid="after-saving-option-move-to-next-animal">
                Move to next animal
              </option>
            </select>
          )}
        />
      </div>
    </div>
  );
};

export default Dosing;
